import { create } from 'zustand';
import type { PicklistMrsSummaryDto } from '../../types';
import { picklistService } from '../../services';

/** What a chip knows about its MRS — everything the card shows before the figures load. */
export interface MrsChipInfo {
  picklistId: number;
  picklistCreatedAt: string;
  materialRequestId: number;
  mrsNumber: string;
  /** Null or '' when the line carries none (the Closed pages' thin lines); the card then uses the summary's. */
  requestorFullName: string | null;
  departmentName: string | null;
}

/** One picklist's summaries in the per-page cache. */
export interface MrsSummaryEntry {
  status: 'loading' | 'ready' | 'error';
  summaries: PicklistMrsSummaryDto[];
  fetchedAt: number;
}

interface MrsHoverCardState {
  /** The open chip's element and facts, or null. Only the host subscribes to this. */
  openChip: { element: HTMLElement; info: MrsChipInfo } | null;
  /** Per-page cache, keyed by picklist id; cleared when the host unmounts. */
  cache: Record<number, MrsSummaryEntry>;
  open: (element: HTMLElement, info: MrsChipInfo) => void;
  /** Close 120 ms from now — the pointer left the chip or the card. Entering either again cancels it. */
  scheduleClose: () => void;
  cancelClose: () => void;
  close: () => void;
  /** A chip leaving the DOM closes the card only when it is the open chip. */
  closeIfOpenChip: (element: HTMLElement) => void;
  /**
   * The detail page's by-id read carries the summaries; seed them. An empty array counts as none and never
   * overwrites a loaded entry: every picklist has an MRS, and the raw-read mutation answers carry `[]`.
   */
  seedSummaries: (picklistId: number, summaries: PicklistMrsSummaryDto[] | undefined) => void;
  /** Lazy load (B4): one request per picklist in flight, reused for 60 s; a failed entry retries on hover. */
  ensureSummaries: (picklistId: number) => void;
  /** The host unmounted: close the card and clear the page's cache. */
  reset: () => void;
}

const CLOSE_DELAY_MS = 120;
const REUSE_MS = 60_000;

let closeTimer: ReturnType<typeof setTimeout> | undefined;
/** Bumped on reset, so a fetch that lands after its page is gone writes nothing. */
let generation = 0;

const clearCloseTimer = () => {
  if (closeTimer !== undefined) {
    clearTimeout(closeTimer);
    closeTimer = undefined;
  }
};

/**
 * PT-7 B1/B3 — the one card's open state and the per-page summary cache.
 *
 * ⚠️ Chips call these actions through `useMrsHoverCardStore.getState()` and subscribe to NOTHING, so opening
 * the card re-renders the card and nothing else. The host derives from directly-subscribed selector values,
 * never from a getter called in render (86d3d1z40).
 */
export const useMrsHoverCardStore = create<MrsHoverCardState>()((set, get) => ({
  openChip: null,
  cache: {},

  open: (element, info) => {
    clearCloseTimer();
    const current = get().openChip;
    if (current && current.element === element) return;
    set({ openChip: { element, info } });
  },

  scheduleClose: () => {
    clearCloseTimer();
    closeTimer = setTimeout(() => {
      closeTimer = undefined;
      set({ openChip: null });
    }, CLOSE_DELAY_MS);
  },

  cancelClose: clearCloseTimer,

  close: () => {
    clearCloseTimer();
    if (get().openChip) set({ openChip: null });
  },

  closeIfOpenChip: (element) => {
    if (get().openChip?.element === element) get().close();
  },

  seedSummaries: (picklistId, summaries) => {
    if (!summaries || summaries.length === 0) return;
    set((state) => ({
      cache: { ...state.cache, [picklistId]: { status: 'ready', summaries, fetchedAt: Date.now() } },
    }));
  },

  ensureSummaries: (picklistId) => {
    const entry = get().cache[picklistId];
    if (entry?.status === 'loading') return;
    if (entry?.status === 'ready' && Date.now() - entry.fetchedAt < REUSE_MS) return;

    const previous = entry?.summaries ?? [];
    const gen = generation;
    set((state) => ({
      cache: { ...state.cache, [picklistId]: { status: 'loading', summaries: previous, fetchedAt: entry?.fetchedAt ?? 0 } },
    }));

    picklistService.getMrsSummaries(picklistId).then(
      (summaries) => {
        if (gen !== generation) return;
        // [] never overwrites figures already in hand.
        const kept = summaries.length > 0 ? summaries : previous;
        set((state) => ({
          cache: { ...state.cache, [picklistId]: { status: 'ready', summaries: kept, fetchedAt: Date.now() } },
        }));
      },
      () => {
        if (gen !== generation) return;
        // Figures already in hand stay on screen; with none, the card says it could not load, and the next
        // hover retries (an error entry is never reused).
        set((state) => ({
          cache: {
            ...state.cache,
            [picklistId]: previous.length > 0
              ? { status: 'ready', summaries: previous, fetchedAt: entry?.fetchedAt ?? 0 }
              : { status: 'error', summaries: [], fetchedAt: Date.now() },
          },
        }));
      },
    );
  },

  reset: () => {
    clearCloseTimer();
    generation += 1;
    set({ openChip: null, cache: {} });
  },
}));

/**
 * PT-5's Escape guard reads this in its handler — never in render (86d3d1z40) — to keep its dialog open while
 * an Escape closes the card first.
 */
export function isMrsHoverCardOpen(): boolean {
  return useMrsHoverCardStore.getState().openChip !== null;
}
